/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the context for the currently loaded form.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used for typing request states.
 */
import { IRequestState } from '@ngt/request-utilities';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to get access to backend types.
 */
import * as Dtos from '../../api/dtos';

/*
 * Used to set default values for the context
 */
import { initialIndividualFormState } from '../../store/modules/data/form';


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IFormContext {
    form: Dtos.IForm | null;
    loadState: IRequestState<Dtos.ResponseStatus>,
    saveState: IRequestState<Dtos.ResponseStatus>,
    actions: IFormActions;
}

interface IFormActions {
    load: () => void;
    save: (form?: Dtos.IForm) => void;
    clear: () => void;
    asyncSave: (form?: Dtos.IForm) => Promise<Dtos.IForm | undefined>;
}

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const FormContext = React.createContext<IFormContext>({
    form: initialIndividualFormState.form,
    loadState: initialIndividualFormState.loadState,
    saveState: initialIndividualFormState.saveState,
    actions: {
        load: () => undefined,
        save: (form) => undefined,
        clear: () => undefined,
        asyncSave: (form) => new Promise<Dtos.IForm | undefined>((resolve, reject) => {
            resolve(undefined)
        })
    }
});

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default FormContext;